import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { toast } from 'react-hot-toast';

import FileDownload from '../../shared/assets/svg/download.svg';
import {
  getReceivablesReports,
  getBlockWiseRevenueReports,
  getUnitWiseRevenueReports,
  getInventoryReports
} from '../services/api/implementation/impl';

/**
 * CSV export for Sales Reports primary tabs: Receivables, Revenue (block/unit), Inventory.
 */
const SalesReportCsvDownloadButton = ({
  variant,
  revenueSubTab = 'Blocks wise',
  selectedProjectId = null
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  const buildCsvPayload = async () => {
    switch (variant) {
      case 'receivables': {
        const response = await getReceivablesReports(1, 1000);
        const targets = response?.data || [];
        if (targets.length === 0) return null;

        const headers = [
          'Block',
          'Unit',
          'Customer',
          'Plot Area',
          'Total Payable',
          'Paid',
          'Pending'
        ];

        const rows = targets.map((item) => {
          const paidAmount = (item?.totalPayable || 0) - parseFloat(item?.due_amount || 0);
          return {
            Block: item?.blockName || 'N/A',
            Unit: item?.unitName || 'N/A',
            Customer: item?.leadName || 'N/A',
            'Plot Area': item?.accommodation?.plotArea
              ? `${item.accommodation.plotArea} ${item.accommodation.measurementUnit || ''}`.trim()
              : 'N/A',
            'Total Payable': item?.totalPayable !== undefined ? item.totalPayable : 0,
            Paid: paidAmount !== undefined ? paidAmount : 0,
            Pending: item?.due_amount !== undefined ? parseFloat(item.due_amount) : 0
          };
        });

        return { headers, rows, fileName: 'sales_receivables_reports.csv' };
      }

      case 'revenue': {
        if (revenueSubTab === 'Blocks wise') {
          const response = await getBlockWiseRevenueReports(1, 1000, selectedProjectId);
          const responseData = response?.data || [];
          if (responseData.length === 0) return null;

          const headers = ['Block', 'Units Sold', 'Revenue', '% Revenue Contribution'];
          const rows = responseData.map((item) => ({
            Block: item?.name || 'N/A',
            'Units Sold': item?.unitSold !== undefined ? item.unitSold : 0,
            Revenue: item?.revenue !== undefined ? item.revenue : 0,
            '% Revenue Contribution':
              item?.revenueContribution !== undefined ? item.revenueContribution : 0
          }));

          return { headers, rows, fileName: 'sales_revenue_blocks_wise.csv' };
        }

        const response = await getUnitWiseRevenueReports(1, 1000, selectedProjectId);
        const responseData = response?.data || [];
        if (responseData.length === 0) return null;

        const headers = [
          'Unit Name',
          'Accommodation Name',
          'Revenue',
          '% Revenue Contribution'
        ];
        const rows = responseData.map((item) => ({
          'Unit Name': item?.unitName || 'N/A',
          'Accommodation Name': item?.accommodation_name || 'N/A',
          Revenue: item?.revenue !== undefined ? item.revenue : 0,
          '% Revenue Contribution':
            item?.['% Revenue Contribution'] !== undefined ? item['% Revenue Contribution'] : 0
        }));

        return { headers, rows, fileName: 'sales_revenue_unit_wise.csv' };
      }

      case 'inventory': {
        const response = await getInventoryReports(1, 1000, selectedProjectId);
        const allInventoryData = response?.data || [];
        if (allInventoryData.length === 0) return null;

        const headers = [
          'Accommodation Name',
          'Accommodation Type',
          'Plot Area',
          'No of Units Sold',
          'Units Available',
          'Units Sold Contribution'
        ];

        const rows = allInventoryData.map((item) => ({
          'Accommodation Name': item?.name || 'N/A',
          'Accommodation Type': item?.accomodationType || 'N/A',
          'Plot Area': item?.plotArea || 'N/A',
          'No of Units Sold': item?.unitsSold !== undefined ? item.unitsSold : 0,
          'Units Available': item?.unitsAvailable !== undefined ? item.unitsAvailable : 0,
          'Units Sold Contribution':
            item?.units_sold_contribution !== undefined ? `${item.units_sold_contribution}%` : '0%'
        }));

        const fileName = selectedProjectId
          ? `sales_inventory_reports_project_${selectedProjectId}.csv`
          : 'sales_inventory_reports.csv';

        return { headers, rows, fileName };
      }

      default:
        return null;
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const payload = await buildCsvPayload();

      if (!payload) {
        toast.error('No data available to download.');
        return;
      }

      const { headers, rows, fileName } = payload;
      const csv = Papa.unparse({ fields: headers, data: rows });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Download initiated successfully!');
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      toast.error(
        downloadError?.response?.data?.message ||
          downloadError?.message ||
          'Failed to download the file. Please try again.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }
    return () => clearInterval(interval);
  }, [isDownloading]);

  const downloadingLabel = `Downloading${'.'.repeat(dotCount)}`;

  return (
    <button
      type="button"
      className={`relative flex items-center justify-center ${
        isDownloading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
      onClick={handleDownload}
      disabled={isDownloading}
      aria-label="Download CSV">
      <img height={35} width={35} src={FileDownload} alt="" />
      {isDownloading && (
        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap text-xs text-gray-700 z-10">
          {downloadingLabel}
        </span>
      )}
    </button>
  );
};

export default SalesReportCsvDownloadButton;
